import React, { useEffect } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Card } from '@americanexpress/dls-react';
import { useRouteParams } from '@/context/routeParamsContext';
import { useModal } from '@/context/modalContext';
import { cardArtMapping } from '@/constants/productMapping';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { getCardArtUrl } from '@/helpers/utils';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import {
  useAutoOpenCardPickerStatus,
  useSelectedProductFromUserWallet,
} from '@/hooks/memberHooks';
import { autoOpenedStatusSessionKey } from '@/constants';
import styles from './CardCalloutNotification.module.scss';
import LoungeAccessProductDescription from './LoungeAccessProductDescription';
import { modalTypes } from './modals/constants';
import touchTargetStyles from './TargetTouchSize.module.scss';

const CardCalloutNotification = ({ className }) => {
  const { shouldShowCardPicker } = useAutoOpenCardPickerStatus();
  const { product } = useRouteParams();
  const { locale } = useSelector(getLocaleData);
  const { customTrack } = useAnalyticsContext();
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const urlSpecifiedProductFromUsersWallet = useSelectedProductFromUserWallet(
    product,
    locale
  );
  const imgSrc = getCardArtUrl({
    baseOneCmsImgUrl,
    product: cardArtMapping[product],
    locale,
  });
  const { setModalName } = useModal();
  const { selectCard, productDisplayNames } = useCommonContent();
  const cardSwitchLinkText = selectCard?.link;

  const productName = productDisplayNames[product]?.cardCallOutDisplayName;

  const handleToggleModal = (event) => {
    setModalName(modalTypes.SELECT_CARD_MODAL);
    customTrack({
      event,
      detail: 'card call out notification',
      element: 'change card button',
    });
  };

  useEffect(() => {
    if (
      window.sessionStorage.getItem(autoOpenedStatusSessionKey) !== '1' &&
      shouldShowCardPicker
    ) {
      setModalName(modalTypes.SELECT_CARD_MODAL);
    }
  }, [shouldShowCardPicker, setModalName]);

  return (
    <Card
      className={clsx(styles.cardCalloutNotification, className)}
      aria-label="Card Callout Notification"
      style={{ minWidth: 'unset' }}
    >
      <img
        src={urlSpecifiedProductFromUsersWallet?.cardArtUrl || imgSrc}
        alt={productName ? `${productName}` : 'Card'}
        className={styles.cardArt}
      />
      <div
        className={clsx(
          'dls-gray-06 tw-text-xs md_tw-text-base',
          styles.cardCalloutInfo
        )}
      >
        <div>
          <span>
            <LoungeAccessProductDescription locale={locale} product={product} />
          </span>
          <button
            type="button"
            className="tw-text-xs tw-text-link tw-underline md_tw-hidden"
            onClick={handleToggleModal}
          >
            {cardSwitchLinkText}
          </button>
        </div>
        <button
          type="button"
          className={clsx(
            'tw-hidden tw-text-base tw-text-link tw-underline md_tw-block',
            touchTargetStyles.touchTarget
          )}
          onClick={handleToggleModal}
        >
          {cardSwitchLinkText}
        </button>
      </div>
    </Card>
  );
};

CardCalloutNotification.propTypes = {
  className: PropTypes.string,
};

export default CardCalloutNotification;
